import { Box } from 'src/design/atoms/Box';
import { Center } from 'src/design/atoms/Center';
import { Group } from 'src/design/atoms/Group';
import { ScrollArea } from 'src/design/atoms/ScrollArea';
import { Skeleton } from 'src/design/atoms/Skeleton';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { IChatUiMessage, useChat } from 'src/hooks/useChat';
import { MessageBubble } from 'src/app/chat/MessageBubble';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { FireIcon } from 'src/design/atoms/FireIcon';
import { getSuggestedPrompts } from 'src/app/chat/suggestedPrompts';
import classes from './chat.module.css';

export interface IMessageListProps {
  messages: IChatUiMessage[];
  loading?: boolean;
  onRefClick?: () => void;
}

// Alternating widths/alignment loosely mimic a short back-and-forth so the
// skeleton doesn't read as a single monotonous block.
const SKELETON_BUBBLES = [
  { isUser: true, width: '45%' },
  { isUser: false, width: '70%' },
  { isUser: true, width: '30%' },
  { isUser: false, width: '55%' },
];

/** @registry Auto-scrolling list of MessageBubbles with a skeleton loading state and empty-state suggested prompts; used inside ChatPanel. */
export const MessageList = React.memo<IMessageListProps>(
  ({ messages, loading, onRefClick }) => {
    const {
      sendMessage,
      regenerate,
      forkAt,
      busy,
      forkingMessageId,
      jobId,
      conversationId,
    } = useChat();
    const viewportRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const viewport = viewportRef.current;
      if (!viewport) return;
      viewport.scrollTo({
        top: viewport.scrollHeight,
        behavior: 'smooth',
      });
    }, [messages]);

    const lastAssistantId = useMemo(
      () => messages.findLast((m) => m.role === 'assistant')?.id ?? null,
      [messages],
    );
    const lastUserId = useMemo(
      () => messages.findLast((m) => m.role === 'user')?.id ?? null,
      [messages],
    );

    const editAndResend = useCallback(
      (messageId: string, text: string) =>
        void sendMessage(text, { replaceFromMessageId: messageId }),
      [sendMessage],
    );

    const continueReply = useCallback(
      () => void sendMessage('Continue'),
      [sendMessage],
    );

    const prompts = useMemo(() => getSuggestedPrompts({ jobId }), [jobId]);

    if (loading) {
      return (
        <Stack gap="md" p="md" data-testid="message-list-skeleton">
          {SKELETON_BUBBLES.map((bubble, index) => (
            <Box
              key={index}
              style={{
                display: 'flex',
                justifyContent: bubble.isUser ? 'flex-end' : 'flex-start',
              }}
            >
              <Skeleton height={36} width={bubble.width} radius="md" />
            </Box>
          ))}
        </Stack>
      );
    }

    if (messages.length === 0) {
      return (
        <Center h="100%" p="md">
          <Stack align="center" gap="xs">
            <Box c="blue.9">
              <FireIcon size={72} />
            </Box>
            <Text fw={700} c="blue.9" className={classes.wordmark}>
              F.I.R.E.
            </Text>
            <Text size="xs" c="dimmed" tt="uppercase">
              Firetrol Intelligent Response Engine
            </Text>
            <Text size="sm" c="dimmed">
              Ask F.I.R.E. a question to get started.
            </Text>
            <Group justify="center" gap="xs" mt="sm">
              {prompts.map((prompt) => (
                <BasicButton
                  key={prompt.prompt}
                  variant="light"
                  size="xs"
                  radius="xl"
                  onClick={() => sendMessage(prompt.prompt)}
                >
                  {prompt.label}
                </BasicButton>
              ))}
            </Group>
          </Stack>
        </Center>
      );
    }

    return (
      <ScrollArea h="100%" viewportRef={viewportRef}>
        <Stack gap="md" p="md">
          {messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              isLastAssistant={message.id === lastAssistantId}
              isLastUser={message.id === lastUserId}
              busy={busy}
              forking={message.id === forkingMessageId}
              onRegenerate={regenerate}
              onContinue={continueReply}
              onEdit={editAndResend}
              onFork={forkAt}
              conversationId={conversationId}
              onRefClick={onRefClick}
            />
          ))}
        </Stack>
      </ScrollArea>
    );
  },
);
